import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Button,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Toolbar,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

import { useSession } from 'store';
import { Page } from 'components';
import { AggReport } from './components';
import { callApi, formatFieldName } from 'utils';

const defaultFilter = {idtype: 'parent', id: '', q: '', start_date: null, end_date: null};
const defaultLimit = 100;

const DailyReports = () => {
  const session = useSession((state) => state.session);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState(() => Object.fromEntries(
    Object.keys(defaultFilter).map(k => {
      let v;
      if (k.endsWith('_date')) {
        v = searchParams.get(k.slice(0, -5));
        v = v ? dayjs(v) : null;
      } else {
        v = searchParams.get(k) || defaultFilter[k];
      }
      return [k, v];
    })
  ));
  const [agg, setAgg] = useState('');

  const dim = location.pathname.slice(9) || 'jobdaily'; // /reports/abc...
  const isAdmin = session.role === 'admin';
  const page = searchParams.get('page')|0;
  const limit = (searchParams.get('limit')|0) || 100;

  useEffect(() => {
    const clone = new URLSearchParams(searchParams);
    clone.get('limit') || clone.set('limit', defaultLimit);
    callApi(`/reports/${dim}?${clone.toString()}`).then(setData).catch(console.error);
  }, [dim, searchParams]);

  const closeModal = useCallback(() => setAgg(''), []);

  const handleFilter = useCallback((ev) => setFilter(prev => ({...prev, [ev.target.name]: ev.target.value})), []);
  const changeDate = (name, val) => setFilter(prev => ({...prev, [name]: val}));
  const submitFilter = () => {
    const prev = new URLSearchParams(searchParams);
    prev.delete('page');
    prev.delete('limit');

    const sp = new URLSearchParams();
    if (filter.id) {
      sp.set('idtype', filter.idtype);
      sp.set('id', filter.id.trim());
    }
    filter.q.trim() && sp.set('q', filter.q.trim());
    filter.start_date && sp.set('start', filter.start_date.format('YYYY-MM-DD'));
    filter.end_date && sp.set('end', filter.end_date.format('YYYY-MM-DD'));

    if (prev.toString() !== sp.toString()) {
      const limit = searchParams.get('limit');
      if (limit) sp.set('limit', limit);
      setSearchParams(sp);
    }
  };
  const resetFilter = () => setFilter({...defaultFilter});

  const handlePagination = (k, v) => {
    setSearchParams((prev) => {
      if (k === 'page') {
        v > 0 ? prev.set(k, v) : prev.delete(k);
      } else {
        v !== defaultLimit ? prev.set(k, v) : prev.delete(k);
        prev.delete('page'); // return from first page if change limit
      }
      return prev;
    });
  };

  const title = data?.title || 'Report';
  return (
    <Page title={title}>
      <Toolbar sx={{mt: -1}}>
        <Select
          name="idtype"
          value={filter.idtype}
          size="small"
          onChange={handleFilter}
          sx={{ mr: 0.5, '& .MuiSelect-select': {py: '6px'} }}
        >
          <MenuItem value='parent'>订单ID</MenuItem>
          <MenuItem value='id'>任务ID</MenuItem>
        </Select>:
        <OutlinedInput
          sx={{mx: 0.5, p: 0, width: 120}}
          name="id"
          value={filter.id}
          size="small"
          inputProps={{ style: {padding: '6px'} }}
          onChange={handleFilter}
        />
        <Typography sx={{mr: 0.5}}>名称:</Typography>
        <OutlinedInput
          sx={{mx: 0.5, p: 0, width: 120}}
          name="q"
          value={filter.q}
          size="small"
          inputProps={{ style: {padding: '6px'} }}
          onChange={handleFilter}
        />
        <Typography sx={{mr: 0.5}}>日期:</Typography>
        <DatePicker
          sx={{ width: 150, '& .MuiInputBase-input': {p: '6px'} }}
          value={filter.start_date}
          views={['year', 'month', 'day']}
          onChange={val => changeDate('start_date', val)}
          dayOfWeekFormatter={(_, date) => dayjs(date).day() || 7}
        />
        <span style={{margin: '0 4px', display: 'inline-flex', alignItems: 'center' }}>~</span>
        <DatePicker
          sx={{ width: 150, '& .MuiInputBase-input': {p: '6px'} }}
          value={filter.end_date}
          views={['year', 'month', 'day']}
          minDate={filter.start_date}
          onChange={val => changeDate('end_date', val)}
          dayOfWeekFormatter={(_, date) => dayjs(date).day() || 7}
        />
        <Button
          sx={{ mx: 0.5 }}
          variant="outlined"
          size="small"
          onClick={submitFilter}
        >搜索</Button>
        <Button
          variant="outlined"
          size="small"
          onClick={resetFilter}
        >重置</Button>
        <Typography sx={{flexGrow: 1}}></Typography>
        {isAdmin && ([
        <Button color="inherit" key="d" onClick={() => setAgg('daily')}>Daily</Button>,
        <Button color="inherit" key="m" onClick={() => setAgg('monthly')}>Monthly</Button>,
        <Button color="inherit" key="p" onClick={() => setAgg('plan')}>Plan</Button>,
        ])}
      </Toolbar>
      <Paper sx={{p: 1, pb: 0, m: 2, mt: -1}}>
        <TableContainer sx={{overflowX: "initial"}}>
          <Table stickyHeader sx={{
            minWidth: 650,
            '& th,td': {
              padding: 1,
              textAlign: 'center',
              whiteSpace: 'pre-line',
            }
          }}>
            <TableHead>
              <TableRow>
                {data?.columns.map(col => (<TableCell key={col}>{formatFieldName(col)}</TableCell>))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!data?.rows.length && (<TableRow><TableCell colSpan={data?.columns.length || 1}>No data</TableCell></TableRow>)}
              {data?.rows.map(row => (
                <TableRow hover key={row.id}>
                  {data?.columns.map(col => (<TableCell key={col}>{row[col]}</TableCell>))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {data?.total > 0 && (
        <TablePagination
          component="div"
          count={data?.total || 0}
          onPageChange={(_, page) => handlePagination('page', page)}
          onRowsPerPageChange={(ev) => handlePagination('limit', ev.target.value)}
          page={page}
          rowsPerPage={limit}
          rowsPerPageOptions={[50, 100, 500]}
        />
        )}
      </Paper>
      <AggReport agg={agg} onClose={closeModal} />
    </Page>
  );
};

export default DailyReports;
